// These ratios are slightly altered above true
// To ensure there and back again conversion from an imperial front end and a metric database
// You break it, you buy it - Chris
export const KG_PER_LB = 0.4535937;
export const LB_PER_KG = 2.20465;
export const CM_PER_INCH = 2.54;
export const INCH_PER_CM = 0.393701;
export const applyWeightTarget = (anthropometrySnapshot, weightTarget) => {
    if (weightTarget) {
        if (anthropometrySnapshot.leanBodyMass) {
            return {
                ...anthropometrySnapshot,
                weight: weightTarget,
                leanBodyMass: (anthropometrySnapshot.leanBodyMass / anthropometrySnapshot.weight) * weightTarget,
            };
        }
        else {
            return { ...anthropometrySnapshot, weight: weightTarget };
        }
    }
    else {
        return anthropometrySnapshot;
    }
};
// same function as above utilizing metric properties
export const applyMetricWeightTarget = (anthropometrySnapshot, weightTargetInKg) => {
    if (weightTargetInKg) {
        // weightTarget can be asserted due to above check
        const imperialWeightTarget = measurementConversionToImperialNonNull(true, weightTargetInKg, "weight");
        const imperialWeight = measurementConversionToImperialNonNull(true, anthropometrySnapshot.weightInKg, "weight");
        if (anthropometrySnapshot.leanBodyMassInKg) {
            // leanBody mass can be asserted due to above check
            const imperialLeanBodyMass = measurementConversionToImperialNonNull(true, anthropometrySnapshot.leanBodyMassInKg, "weight");
            return {
                ...anthropometrySnapshot,
                weight: imperialWeightTarget,
                weightInKg: weightTargetInKg,
                leanBodyMass: (imperialLeanBodyMass / imperialWeight) * imperialWeightTarget,
                leanBodyMassInKg: (anthropometrySnapshot.leanBodyMassInKg / anthropometrySnapshot.weightInKg) * weightTargetInKg,
            };
        }
        else {
            return { ...anthropometrySnapshot, weightInKg: weightTargetInKg, weight: imperialWeightTarget };
        }
    }
    else {
        return anthropometrySnapshot;
    }
};
export const weightToKg = (weight) => weight / LB_PER_KG;
export const weightToLbs = (weight) => weight * LB_PER_KG;
export const heightToCm = (height) => height * CM_PER_INCH;
export const getLeanBodyMass = (weight, percentBodyFat) => percentBodyFat ? weight - weight * (percentBodyFat / 100) : null;
export const getPercentBodyFat = (weight, leanBodyMass) => leanBodyMass ? ((weight - leanBodyMass) / weight) * 100 : null;
export const sexToText = (sex) => {
    switch (sex) {
        case "male":
            return "Male";
        case "female":
            return "Female";
        default:
            return "Other";
    }
};
export const measurementConversionToMetric = (isMetric, value, prop) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (isMetric) {
        return value;
    }
    if (prop === "length") {
        return value * CM_PER_INCH;
    }
    else {
        return value * KG_PER_LB;
    }
};
export const measurementConversionToMetricNonNull = (isMetric, value, prop) => {
    if (isMetric) {
        return value;
    }
    if (prop === "length") {
        return value * CM_PER_INCH;
    }
    else {
        return value * KG_PER_LB;
    }
};
// should this have a non-nullable version?
export const measurementConversionToImperial = (isMetric, value, prop) => {
    if (!isMetric || value === null) {
        return value;
    }
    if (prop === "length") {
        return value * INCH_PER_CM;
    }
    else {
        return value * LB_PER_KG;
    }
};
export const measurementConversionToImperialNonNull = (isMetric, value, prop) => {
    if (!isMetric) {
        return value;
    }
    if (prop === "length") {
        return value * INCH_PER_CM;
    }
    else {
        return value * LB_PER_KG;
    }
};
export const getWeightUnitForLocale = (isMetricLocale) => (isMetricLocale ? "kg" : "lb");
export const getHeightUnitForLocale = (isMetricLocale) => (isMetricLocale ? "cm" : "in");
export const formatImperialHeight = (height) => {
    const roundedHeight = Math.floor(height);
    const feet = Math.floor(roundedHeight / 12);
    const inches = roundedHeight - 12 * feet;
    return `${feet}'${inches}"`;
};
export const formatImperialWeight = (weight) => roundToHundredthsFloor(weight) + " lb";
export const formatMetricWeight = (weight) => roundToHundredthsFloor(weight) + " kg";
export const formatMetricHeight = (height) => roundToHundredthsFloor(height) + " cm";
// creates whole number if x.00
export const roundToHundredthsFloor = (input) => Math.floor(input * 100) / 100;
// creates whole number if x.00
export const maybeRoundToHundredthsFloor = (input) => (input ? Math.floor(input * 100) / 100 : null);
export const convertAnthropometryEntryToSnapshot = (entry) => {
    const { weight, height, leanBodyMass, percentBodyFat, sex, isMetric } = entry;
    return {
        weight: measurementConversionToImperialNonNull(isMetric, weight, "weight"),
        height: measurementConversionToImperial(isMetric, height, "length"),
        leanBodyMass: measurementConversionToImperial(isMetric, leanBodyMass, "weight"),
        weightInKg: measurementConversionToMetricNonNull(isMetric, weight, "weight"),
        heightInCm: measurementConversionToMetric(isMetric, height, "length"),
        leanBodyMassInKg: measurementConversionToMetric(isMetric, leanBodyMass, "weight"),
        percentBodyFat,
        sex,
        age: null,
    };
};
const bodyFatPercentage = (value) => {
    return value / 100;
};
/**
 * Calculates Body Fat Mass (BFM) using the formula:
 * BFM = Weight × (Body Fat % / 100)
 *
 * Example:
 * If someone weighs 100 lbs and has 18% body fat:
 * BFM = 100 × 18% = 18 lbs of fat mass
 */
export const calculateBodyFatMass = (weight, percentBodyFat) => {
    return weight * bodyFatPercentage(percentBodyFat);
};
/**
 * Calculates Fat-Free Mass (FFM) using the formula:
 * FFM = Total Body Weight − Body Fat Mass
 *
 * Example:
 * If someone weighs 100 lbs and has 18 lbs of fat mass:
 * FFM = 100 - 18 = 82 lbs of fat-free mass
 */
export const calculateLeanBodyMass = (weight, bodyFatMass) => {
    return weight - bodyFatMass;
};
export const calculateBodyComposition = (weight, percentBodyFat) => {
    const bodyFatMass = calculateBodyFatMass(weight, percentBodyFat);
    const leanBodyMass = calculateLeanBodyMass(weight, bodyFatMass);
    return {
        bodyFatMass: bodyFatMass,
        leanBodyMass: leanBodyMass,
    };
};
//# sourceMappingURL=Anthropometry.js.map